/**
 * MAZE PATROL — cartridge palette.
 *
 * Museum-at-night color script. Navy corridor walls, warm gold security
 * badges scattered across the floor, neon-pink alarm wash when the
 * player triggers an alarm panel (power pellet). The cartridge renders
 * a centered 270×270 maze inside the 480×270 landscape canvas per the
 * cabinet's landscape-only rule; gutter chrome (HUD + minimap + rotation
 * countdown) shares the museum palette so the screen reads as one
 * coherent surveillance feed, not "game + black bars."
 */

// Background — deep midnight blue, the lights-out museum gallery.
export const FIELD_BG = '#08081e';

// Gutter wash — a touch warmer than FIELD_BG so the maze reads as a
// slight inset / "monitor screen" inside the larger control room.
export const GUTTER_BG = '#0c0c24';

// Walls — corridor structure. Two tones for face vs edge so the maze
// has a sense of depth, not flat tiles.
export const WALL = '#3a52c8';
export const WALL_EDGE = '#1a2470';
export const WALL_GLOW = 'rgba(58, 82, 200, 0.4)';

// Badges (dots) — the museum security checkpoints. Gold so they feel
// like collectible value, not generic dots.
export const BADGE = '#ffc834';
export const BADGE_DARK = '#a07a10';

// Alarm panel (power pellet) — pulses pink when armed, dark when used.
export const ALARM_PANEL = '#ff40c0';
export const ALARM_PANEL_DIM = '#5a1a48';

// Alarm-active wash — the canvas tints toward neon-pink during the 8s
// alarm window. Subtle overlay, not full blanket, so the maze stays
// readable.
export const ALARM_WASH = 'rgba(255, 64, 192, 0.12)';

// Player — security guard. Cyan body with a yellow flashlight cone so
// they pop against the navy maze.
export const PLAYER_BODY = '#3fb8e6';
export const PLAYER_OUTLINE = '#0c4e6a';
export const PLAYER_LIGHT = 'rgba(255, 234, 0, 0.35)';

// Thieves — four distinct colors so the player can read which AI is
// which at a glance (Pac-Man's ghost-color convention). Each color
// roughly indicates threat level:
//   - PURSUER: red — direct chase
//   - AMBUSHER: magenta — predictive
//   - PATROLLER: green — fixed waypoints
//   - WANDERER: orange — random
export const THIEF_PURSUER = '#e63a3a';
export const THIEF_AMBUSHER = '#d83ad8';
export const THIEF_PATROLLER = '#3ad860';
export const THIEF_WANDERER = '#ff9020';
export const THIEF_SCARED = '#5a78ff'; // all thieves recolor while alarm is active
export const THIEF_SCARED_FLASH = '#ffffff'; // flicker in the last ~1.5s of alarm
export const THIEF_EYES = '#ffffff';
export const THIEF_PUPIL = '#08081e';

// Jail (front desk) — center cell thieves return to when tagged.
export const JAIL_BG = '#2a2a48';
export const JAIL_BAR = '#5a5a78';

// HUD typography.
export const HUD_LABEL = '#7c8089';
export const HUD_SCORE = '#ffc834';
export const HUD_LIVES = '#3fb8e6';
export const HUD_LEVEL = '#ff9020';
export const HUD_ALERT = '#ff3030';
export const HUD_ALARM_BAR = '#ff40c0';

// Effects.
export const FLASH_HIT = '#ffffff';
export const ROTATION_WASH = 'rgba(255, 234, 0, 0.18)';

/** Tokens the cabinet samples for marquee tinting while this cartridge
 *  is loaded. First entry is `paletteHint[0]`, the cartridge's hero
 *  color — used everywhere the cabinet needs a single signature pick. */
export const PALETTE_HINT: readonly string[] = [
  ALARM_PANEL, // neon-pink — the cartridge's iconic moment
  BADGE,
  WALL,
  PLAYER_BODY,
];
